<template>
  <article class="terrain-section-editor">
    <header class="terrain-section-editor__header">
      <div class="terrain-section-editor__fields">
        <label>
          Título de la sección
          <input :value="section.title" @input="patchSection({ title: $event.target.value })">
        </label>
        <label>
          Descripción
          <textarea :value="section.description" @input="patchSection({ description: $event.target.value })"></textarea>
        </label>
      </div>
      <div class="terrain-section-editor__actions" aria-label="Acciones de sección">
        <button type="button" :disabled="isFirst" @click="$emit('move', { sectionId: section.sectionId, direction: -1 })">Subir</button>
        <button type="button" :disabled="isLast" @click="$emit('move', { sectionId: section.sectionId, direction: 1 })">Bajar</button>
        <button type="button" @click="$emit('duplicate', section.sectionId)">Duplicar</button>
        <button type="button" class="terrain-section-editor__danger" @click="$emit('remove', section.sectionId)">Eliminar</button>
      </div>
    </header>

    <ConditionEditor :value="section.visibility || null" :definition="definition" :target-section-id="section.sectionId" @input="patchSection({ visibility: $event })" />

    <div class="terrain-section-editor__questions">
      <QuestionEditor
        v-for="question in section.questions"
        :key="question.questionId"
        :question="question"
        :definition="definition"
        :section-id="section.sectionId"
        :section-conditional="!!section.visibility"
        :is-first="isFirstQuestion(question.questionId)"
        :is-last="isLastQuestion(question.questionId)"
        @update="updateQuestion"
        @duplicate="duplicateQuestion"
        @remove="removeQuestion"
        @move="moveQuestion"
      />
    </div>

    <label class="terrain-section-editor__add-question">
      Agregar pregunta
      <TerrainSelect v-model="newQuestionType" :options="questionTypes" aria-label="Tipo de pregunta nueva" />
      <button type="button" @click="addQuestion">Agregar</button>
    </label>
  </article>
</template>

<script>
import QuestionEditor from "./QuestionEditor.vue";
import TerrainSelect from "@/components/terrain/TerrainSelect.vue";
import { cloneQuestion, createQuestion } from "./factories.js";
import ConditionEditor from "./ConditionEditor.vue";

export default {
  name: "SectionEditor",
  components: { QuestionEditor, TerrainSelect, ConditionEditor },
  props: {
    section: { type: Object, required: true },
    definition: { type: Object, required: true },
    isFirst: { type: Boolean, default: false },
    isLast: { type: Boolean, default: false },
  },
  data() {
    return {
      newQuestionType: "shortText",
      questionTypes: [
        { value: "shortText", label: "Texto corto" }, { value: "longText", label: "Texto largo" },
        { value: "number", label: "Número" }, { value: "date", label: "Fecha" },
        { value: "singleSelect", label: "Selección simple" }, { value: "multiSelect", label: "Selección múltiple" },
        { value: "check", label: "Check" }, { value: "table", label: "Tabla" },
        { value: "repeater", label: "Repetidor" }, { value: "location", label: "Ubicación" },
        { value: "file", label: "Archivo" }, { value: "photo", label: "Foto" },
      ],
    };
  },
  methods: {
    emitSection(questions = this.section.questions, patch = {}) {
      this.$emit("update", { ...this.section, ...patch, questions });
    },
    patchSection(patch) { this.emitSection(this.section.questions, patch); },
    addQuestion() { this.emitSection([...this.section.questions, createQuestion(this.newQuestionType)]); },
    updateQuestion(updatedQuestion) {
      this.emitSection(this.section.questions.map((question) => question.questionId === updatedQuestion.questionId ? updatedQuestion : question));
    },
    duplicateQuestion(questionId) {
      const source = this.section.questions.find((question) => question.questionId === questionId);
      if (!source) return;
      const questions = [...this.section.questions];
      questions.splice(questions.findIndex((question) => question.questionId === questionId) + 1, 0, cloneQuestion(source));
      this.emitSection(questions);
    },
    removeQuestion(questionId) { this.emitSection(this.section.questions.filter((question) => question.questionId !== questionId)); },
    moveQuestion({ questionId, direction }) {
      const questions = [...this.section.questions];
      const currentIndex = questions.findIndex((question) => question.questionId === questionId);
      const targetIndex = currentIndex + direction;
      if (currentIndex < 0 || targetIndex < 0 || targetIndex >= questions.length) return;
      const [question] = questions.splice(currentIndex, 1);
      questions.splice(targetIndex, 0, question);
      this.emitSection(questions);
    },
    isFirstQuestion(questionId) { return this.section.questions[0] && this.section.questions[0].questionId === questionId; },
    isLastQuestion(questionId) { const questions = this.section.questions; return questions.length > 0 && questions[questions.length - 1].questionId === questionId; },
  },
};
</script>

<style scoped lang="scss">
.terrain-section-editor {
  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-left: 4px solid var(--color-steel);
  border-radius: var(--terrain-radius-lg);
  padding: var(--terrain-space-lg);
  display: grid;
  gap: var(--terrain-space-md);
  box-shadow: var(--terrain-shadow-sm);
}

.terrain-section-editor__header { display: flex; gap: var(--terrain-space-lg); justify-content: space-between; }
.terrain-section-editor__fields { display: grid; gap: var(--terrain-space-sm); flex: 1; }
.terrain-section-editor label { display: grid; gap: var(--terrain-space-xs); color: var(--color-navy); font-weight: 600; font-size: var(--terrain-font-caption); }

input, textarea {
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  font: inherit;
  padding: var(--terrain-space-sm);
  transition: border-color var(--terrain-transition), box-shadow var(--terrain-transition);

  &:hover { border-color: var(--color-steel); }
  &:focus { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); outline: none; }
}

textarea { min-height: 52px; resize: vertical; }

.terrain-section-editor__actions { display: flex; align-items: flex-start; flex-wrap: wrap; gap: var(--terrain-space-xs); }

button {
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  background: var(--color-white);
  color: var(--color-navy);
  cursor: pointer;
  font-weight: 600;
  padding: var(--terrain-space-xs) var(--terrain-space-sm);
  transition: background-color var(--terrain-transition), border-color var(--terrain-transition);

  &:hover:not(:disabled) { background: var(--color-mist); border-color: var(--color-steel); }
  &:disabled { cursor: not-allowed; opacity: .45; }
}

.terrain-section-editor__danger { color: var(--color-danger); }
.terrain-section-editor__danger:hover:not(:disabled) { background: var(--terrain-color-danger-light); border-color: var(--color-danger); }
.terrain-section-editor__questions { display: grid; gap: var(--terrain-space-sm); }
.terrain-section-editor__add-question { grid-template-columns: auto minmax(170px, 1fr) auto; align-items: center; }
@media (max-width: 700px) { .terrain-section-editor__header, .terrain-section-editor__add-question { display: grid; grid-template-columns: 1fr; } }
</style>
